import { useIntl } from 'gatsby-plugin-react-intl';
import PropTypes from 'prop-types';
import { useContext, useEffect, useState, useRef } from 'react';

import OutsideClickHandler from '../../../utils/outsideClickHandler';
import NotificationBanner from '../../Custom/Banners/NotificationBanner';
import Blade from '../../Custom/Blade';
import GlobalSearch from '../../Custom/GlobalSearch';
import IndividualBlade from '../../Custom/IndividualBlades';
import MainMenu from '../../Custom/MainMenu';
import { BannerContext } from '../../Providers/Banner';
import { Context } from '../../Providers/Blade';
import Container from '../Container';
import LanguageToggle from '../LanguageToggle';
import NavigationTabs from '../NavigationTabs';
import RichText, { fragment } from '../RichText';

import {
  ApplicationWrapper,
  ApplyNowLink,
  Controls,
  Logo,
  LogoLink,
  MenuBtn,
  NavigationBar,
  SearchDrawer,
  SearchIcon,
  SearchWrapper,
  SkipToContentLink,
  StyledCtas,
  StyledHeader,
  TopNav,
  StickyCtasContainer,
  StyledStickyCtas,
  HeaderCtas,
} from './styles';

const Header = ({
  data,
  banner = [],
  skipToContentFocus = null,
  darkMode,
  contentType = '',
}) => {
  if (!data) return null;
  const {
    brandLogoDam,
    clickableLogo,
    stickyHeader,
    searchFieldText,
    ctas,
    applyNowSection,
    navigationTabs,
    menuBlade,
    stickyCtas,
  } = data;

  const {
    state: { menuState },
    updateMenuState,
  } = useContext(Context);

  const intl = useIntl();
  const searchRef = useRef();

  // Set the viewed state for the notification banner
  const [viewed, setViewed] = useState(true);
  // Set the state of the banner based upon the viewed state
  const {
    state: { notificationBannerState },
    updateNotificationBannerState,
  } = useContext(BannerContext);

  // search drawer state
  const [showSearchDrawer, toggleSearchDrawer] = useState(false);

  // cta slide tracker
  const [slide, setSlide] = useState(false);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      handleScroll();
      window.addEventListener('scroll', handleScroll);
    }
    return () => {
      // Remove all events, since component is no longer mounted.
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  function handleScroll() {
    if (window.scrollY > document.body.scrollHeight * 0.05) {
      setSlide(true);
    } else {
      setSlide(false);
    }
  }

  // Set the various states of the header (sticky) if notification banner is present:
  // If the notification banner is set to fixed:
  // - notification banner will overlap the header
  // If the notification banner is set to not fixed:
  // - notification banner if previously viewed will not show and the header will remain sticky
  // - or -
  // - notification banner if not previously viewed will show and push the entire site down
  // - default -
  // If header is not sticky, notification banner will overlap header or push site down depending on if set to fixed/not fixed
  useEffect(() => {
    if (stickyHeader) {
      if (banner) {
        if (banner.length) {
          if (banner[0].fixed) {
            updateNotificationBannerState('fixed');
          } else if (viewed) {
            updateNotificationBannerState('fixed');
          } else {
            updateNotificationBannerState('notFixed');
          }
        }
      }
    }
  }, [viewed]);

  useEffect(() => {
    if (showSearchDrawer) {
      searchRef.current.focus();
    }
  }, [showSearchDrawer]);

  return (
    <>
      <StyledHeader
        as="header"
        stickyHeader={stickyHeader}
        bannerState={notificationBannerState}
      >
        <SkipToContentLink onClick={skipToContentFocus}>
          {intl.locale === 'en' ? 'Skip to content' : 'Aller au contenu'}
        </SkipToContentLink>
        {banner &&
          banner.map(ban => (
            <NotificationBanner
              key={ban.id}
              data={ban}
              viewed={viewed}
              setViewed={setViewed}
              darkMode={darkMode && darkMode}
            />
          ))}
        <Container type="auto-margin">
          <TopNav>
            {clickableLogo ? (
              <LogoLink to="/">
                <Logo
                  src={brandLogoDam.asset.localImage.publicURL}
                  alt={brandLogoDam.altText || ''}
                />
              </LogoLink>
            ) : (
              <Logo
                src={brandLogoDam.asset.localImage.publicURL}
                alt={brandLogoDam.altText || ''}
              />
            )}
            {navigationTabs && (
              <nav>
                <NavigationBar>
                  <NavigationTabs data={navigationTabs} />
                </NavigationBar>
              </nav>
            )}
            <Controls className={`controls ${contentType}`}>
              <HeaderCtas $footerSticky={stickyCtas}>
                {ctas && <StyledCtas ctas={ctas} direction="row" />}
                {applyNowSection && (
                  <ApplicationWrapper>
                    {applyNowSection.internal &&
                      applyNowSection.internal.type === 'ContentfulCta' && (
                        <ApplyNowLink data={applyNowSection}>
                          <RichText
                            document={applyNowSection.label}
                            options={fragment}
                          />
                        </ApplyNowLink>
                      )}
                    {applyNowSection.productCards && (
                      <IndividualBlade data={applyNowSection} />
                    )}
                  </ApplicationWrapper>
                )}
              </HeaderCtas>
              <LanguageToggle showShortButton />
              {menuBlade && (
                <MenuBtn isOpen={menuState} onClick={updateMenuState}>
                  <RichText document={menuBlade.heading} options={fragment} />
                </MenuBtn>
              )}

              <OutsideClickHandler
                isActive={showSearchDrawer}
                callback={() => toggleSearchDrawer(false)}
              >
                <SearchWrapper>
                  {searchFieldText && (
                    <SearchIcon
                      aria-label={searchFieldText}
                      onClick={() => toggleSearchDrawer(!showSearchDrawer)}
                      onKeyDown={() => toggleSearchDrawer(!showSearchDrawer)}
                    />
                  )}
                  {showSearchDrawer && (
                    <SearchDrawer>
                      <GlobalSearch
                        id="global-search"
                        type="drawer is-open"
                        placeholder={searchFieldText}
                        predictiveSearch
                        ref={searchRef}
                        drawerFunc={toggleSearchDrawer}
                      />
                    </SearchDrawer>
                  )}
                </SearchWrapper>
              </OutsideClickHandler>
            </Controls>
          </TopNav>
        </Container>
        {menuBlade && (
          <Blade
            isOpen={menuState}
            ctas={menuBlade.ctas}
            component={menuBlade.component}
            logo={brandLogoDam}
          >
            <GlobalSearch
              id="main-menu-search"
              type="main-menu is-open"
              placeholder={searchFieldText}
            />
            <MainMenu
              data={{
                ...menuBlade,
                ...{ searchFieldText },
                ...{ brandLogoDam },
              }}
            />
          </Blade>
        )}
      </StyledHeader>
      {stickyCtas && (
        <StickyCtasContainer className={slide ? 'show' : 'hide'}>
          <StyledStickyCtas ctas={stickyCtas} direction="column" />
        </StickyCtasContainer>
      )}
    </>
  );
};

Header.propTypes = {
  data: PropTypes.shape().isRequired,
  banner: PropTypes.arrayOf(PropTypes.shape()),
  skipToContentFocus: PropTypes.func,
  darkMode: PropTypes.bool.isRequired,
  contentType: PropTypes.string,
};

export default Header;
